import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { User, authService } from '../services/authService';
import { Registration, dataService } from '../services/dataService';

interface AppState {
  user: User | null;
  isLoading: boolean;
  registrations: Registration[];
  error: string | null;
  isRegistrationOpen: boolean;
}

type AppAction =
  | { type: 'SET_USER'; payload: User | null }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_REGISTRATIONS'; payload: Registration[] }
  | { type: 'ADD_REGISTRATION'; payload: Registration }
  | { type: 'UPDATE_REGISTRATION'; payload: Registration }
  | { type: 'DELETE_REGISTRATION'; payload: string }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_REGISTRATION_STATUS'; payload: boolean };

const initialState: AppState = {
  user: null,
  isLoading: false,
  registrations: [],
  error: null,
  isRegistrationOpen: true,
};

const appReducer = (state: AppState, action: AppAction): AppState => {
  switch (action.type) {
    case 'SET_USER':
      return { ...state, user: action.payload };
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'SET_REGISTRATIONS':
      return { ...state, registrations: action.payload };
    case 'ADD_REGISTRATION':
      return { ...state, registrations: [...state.registrations, action.payload] };
    case 'UPDATE_REGISTRATION':
      return {
        ...state,
        registrations: state.registrations.map(reg =>
          reg.id === action.payload.id ? action.payload : reg
        ),
      };
    case 'DELETE_REGISTRATION':
      return {
        ...state,
        registrations: state.registrations.filter(reg => reg.id !== action.payload),
      };
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    case 'SET_REGISTRATION_STATUS':
      return { ...state, isRegistrationOpen: action.payload };
    default:
      return state;
  }
};

interface AppContextValue extends AppState {
  login: (email: string, password: string, role: 'coach' | 'headteacher' | 'headmaster' | 'admin', firstName?: string, lastName?: string) => Promise<{ needsRegistration?: boolean }>;
  logout: () => Promise<void>;
  loadRegistrations: (filters?: any) => Promise<void>;
  createRegistration: (formData: any) => Promise<void>;
  updateRegistrationStatus: (id: string, status: 'approved' | 'rejected') => Promise<void>;
  deleteRegistration: (id: string) => Promise<void>;
  toggleRegistrationStatus: () => void;
}

const AppContext = createContext<AppContextValue | undefined>(undefined);

export const useApp = () => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};

export const AppProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(appReducer, initialState);

  useEffect(() => {
    // Check for existing session on app start
    const currentUser = authService.getCurrentUser();
    if (currentUser) {
      dispatch({ type: 'SET_USER', payload: currentUser });
    }
  }, []);

  const login = async (email: string, password: string, role: 'coach' | 'headteacher' | 'headmaster' | 'admin', firstName?: string, lastName?: string) => {
    try {
      dispatch({ type: 'SET_ERROR', payload: null });
      
      const result = await authService.loginWithTeacherRegistration({ 
        email, 
        password, 
        role, 
        firstName, 
        lastName 
      });
      dispatch({ type: 'SET_USER', payload: result.user });
      return { needsRegistration: result.needsRegistration };
    } catch (error) {
      const errorMessage = (error as Error).message;
      if (errorMessage === 'NEEDS_REGISTRATION') {
        dispatch({ type: 'SET_ERROR', payload: null });
        return { needsRegistration: true };
      }
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
      throw error;
    }
  };

  const logout = async () => {
    try {
      await authService.logout();
      dispatch({ type: 'SET_USER', payload: null });
      dispatch({ type: 'SET_REGISTRATIONS', payload: [] });
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: (error as Error).message });
    }
  };

  const loadRegistrations = async (filters?: any) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const registrations = await dataService.getRegistrations(filters);
      dispatch({ type: 'SET_REGISTRATIONS', payload: registrations });
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: (error as Error).message });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const createRegistration = async (formData: any) => {
    try {
      if (!state.user || !state.user.id) throw new Error('User not authenticated');
      
      dispatch({ type: 'SET_LOADING', payload: true });
      const registration = await dataService.createRegistration(formData);
      dispatch({ type: 'ADD_REGISTRATION', payload: registration });
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: (error as Error).message });
      throw error;
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const updateRegistrationStatus = async (id: string, status: 'approved' | 'rejected') => {
    try {
      if (!state.user || !state.user.id) throw new Error('User not authenticated');
      
      dispatch({ type: 'SET_LOADING', payload: true });
      const updatedRegistration = await dataService.updateRegistrationStatus(id, status, state.user.id);
      dispatch({ type: 'UPDATE_REGISTRATION', payload: updatedRegistration });
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: (error as Error).message });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const deleteRegistration = async (id: string) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      await dataService.deleteRegistration(id);
      dispatch({ type: 'DELETE_REGISTRATION', payload: id });
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: (error as Error).message });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const toggleRegistrationStatus = () => {
    dispatch({ type: 'SET_REGISTRATION_STATUS', payload: !state.isRegistrationOpen });
  };

  const value: AppContextValue = {
    ...state,
    login,
    logout,
    loadRegistrations,
    createRegistration,
    updateRegistrationStatus,
    deleteRegistration,
    toggleRegistrationStatus,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};
